/**
 * Employer (seat) paywall on web: the pre-redesign feature-table dialog, kept
 * as-is. The subscription-journeys redesign applies to `planSet: 'individual'`
 * only; `UpgradeContent.web.tsx` delegates here for employer mode.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Check, Minus } from 'lucide-react';
import type { UpgradeContext } from '@tabbio/types';
import { WebDialog } from '../common/WebDialog.web';
import { useToast } from '../../hooks/useToast';
import { useRevenueCat } from '../../hooks/useRevenueCat';
import { useWorkspace } from '../../providers/useWorkspace';
import { useSettingsModal } from '../../providers/useSettingsModal';
import { useSubscriptionDisplay } from '../../lib/subscription-display';
import {
  normalizeUpgradeBillingParam,
  normalizeUpgradeContextParams,
  normalizeUpgradeModeParam,
  normalizeUpgradeTierParam,
} from '../../lib/upgrade-routing';
import {
  captureProductException,
  getErrorObservabilityProperties,
  trackProductEvent,
} from '../../lib/product-analytics';
import {
  type Tier,
  type BillingPeriod,
  type PlanMode,
  type FeatureCell,
  getTierConfigs,
} from './upgrade-data';
import { UpgradeHeroIcon } from './UpgradeHeroIcon';
import {
  getUpgradeAnalyticsProperties,
  persistPendingUpgradeContext,
} from '../../lib/monetization-context';
import { getUpgradeAllowanceCopy } from '../../lib/upgrade-allowance-copy';
import { AgentModelMark } from '../assistant-ui/AgentModelMark';

const HOVER_BG = 'rgba(0,0,0,0.04)';
const FEATURE_SPARK_ICON = require('../../../assets/branding/upgrade-spark.png');
const FEATURE_NOVA_ICON = require('../../../assets/branding/nova-mascot.png');

type FeatureTierMark = 'spark' | 'nova';

const toAssetSrc = (asset: unknown): string =>
  typeof asset === 'string'
    ? asset
    : ((asset as { uri?: string; default?: string } | null)?.uri ??
      (asset as { default?: string } | null)?.default ??
      '');

const FEATURE_SPARK_SRC = toAssetSrc(FEATURE_SPARK_ICON);
const FEATURE_NOVA_SRC = toAssetSrc(FEATURE_NOVA_ICON);

function setHoverBg(e: React.MouseEvent<HTMLElement>, bg: string) {
  e.currentTarget.style.backgroundColor = bg;
}

function getFeatureTierMark(
  tier: Tier,
  featureKey: string,
): FeatureTierMark | null {
  // One agent mark per screen, on the row the pricing spec calls out.
  if (
    tier === 'plus' &&
    (featureKey === 'smarter_ai' || featureKey === 'employer.jd_generator')
  ) {
    return 'spark';
  }

  if (
    tier === 'pro' &&
    (featureKey === 'agent_applies' ||
      featureKey === 'employer.autopilot_hiring')
  ) {
    return 'nova';
  }

  return null;
}

function FeatureTierIcon({ mark }: { mark: FeatureTierMark }) {
  return (
    <img
      src={mark === 'spark' ? FEATURE_SPARK_SRC : FEATURE_NOVA_SRC}
      alt=""
      width={mark === 'spark' ? 22 : 22}
      height={mark === 'spark' ? 22 : 28}
      style={{
        display: 'block',
        flex: '0 0 auto',
        objectFit: 'contain',
      }}
    />
  );
}

function FeatureColumnValue({ value }: { value: FeatureCell }) {
  if (typeof value === 'boolean') {
    return value ? (
      <Check size={18} color="var(--text-primary)" strokeWidth={2.5} />
    ) : (
      <Minus size={18} color="var(--text-tertiary)" strokeWidth={2} />
    );
  }

  return (
    <span
      style={{
        display: 'flex',
        minWidth: 0,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        textAlign: 'center',
        lineHeight: 1.2,
      }}
    >
      {value.modelBrand ? (
        <AgentModelMark brand={value.modelBrand} size={14} />
      ) : null}
      <span
        style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}
      >
        {value.title}
      </span>
      {value.subtitle ? (
        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {value.subtitle}
        </span>
      ) : null}
    </span>
  );
}

export type UpgradeContentProps = {
  mode?: PlanMode;
  context?: UpgradeContext;
  /** Preselects a tier when opened as a dialog (falls back to route params). */
  tier?: Tier;
  /** Preselects a billing period when opened as a dialog (falls back to route params). */
  billing?: BillingPeriod;
  onComplete?: () => void;
  onClose?: () => void;
  /** Renders a quiet "Continue with Free" button in the footer (onboarding paywall skip). */
  onSkip?: () => void;
};

export function EmployerUpgradeContent({
  mode,
  context,
  tier: tierProp,
  billing: billingProp,
  onComplete,
  onClose,
  onSkip,
}: UpgradeContentProps = {}) {
  const { t, i18n } = useTranslation('common');
  const router = useRouter();
  const toast = useToast();
  const { companyId } = useWorkspace();
  const { startPurchase, isBusy } = useRevenueCat();
  const settingsModal = useSettingsModal();
  const subscriptionDisplay = useSubscriptionDisplay('web');
  const params = useLocalSearchParams<{
    mode?: string;
    tier?: string;
    billing?: string;
    placement?: string;
    entryPoint?: string;
    actionKey?: string;
    featureKey?: string;
    reason?: string;
    allowanceRemaining?: string;
    allowanceLimit?: string;
    allowanceUnit?: string;
    resetAt?: string;
    returnTo?: string;
    draftId?: string;
    resumeToken?: string;
  }>();
  const resolvedContext = context ?? normalizeUpgradeContextParams(params);

  const planMode: PlanMode =
    resolvedContext?.planSet ?? mode ?? normalizeUpgradeModeParam(params.mode);
  const tierConfigs = getTierConfigs(planMode, t);
  const requestedTier =
    tierProp ??
    resolvedContext?.recommendedTier ??
    normalizeUpgradeTierParam(params.tier);

  const [activeTier, setActiveTier] = useState<Tier>(requestedTier);
  const [billing, setBilling] = useState<BillingPeriod>(
    billingProp ?? normalizeUpgradeBillingParam(params.billing),
  );
  const config = tierConfigs[activeTier];
  const currentTier =
    subscriptionDisplay.upgradeMode === planMode
      ? subscriptionDisplay.tier
      : 'free';
  const showManageInsteadOfCheckout =
    currentTier === 'pro' || (currentTier === 'plus' && activeTier === 'plus');
  const viewedRef = useRef(false);
  // Per the pricing spec the tier headline is the H1 on every entry point, and
  // the screens carry no subtitle. `upgrade.context.*` copy is retained in the
  // locale files should placement-specific headlines return.
  const contextTitle = config.title;
  const contextSubtitle = '';
  const allowanceCopy = getUpgradeAllowanceCopy(
    resolvedContext,
    t,
    i18n.resolvedLanguage ?? i18n.language ?? 'en',
  );
  const contextAnalytics = getUpgradeAnalyticsProperties(resolvedContext);

  const ctaLabel = subscriptionDisplay.isLoadingSubscriptionState
    ? t('settings.checkingSubscription')
    : showManageInsteadOfCheckout
      ? subscriptionDisplay.subscriptionRowLabel
      : billing === 'yearly'
        ? config.ctaYearly
        : config.ctaMonthly;

  useEffect(() => {
    if (currentTier === 'pro') {
      setActiveTier('pro');
      return;
    }

    if (requestedTier === 'pro') {
      setActiveTier('pro');
    }
  }, [currentTier, requestedTier]);

  useEffect(() => {
    if (viewedRef.current) {
      return;
    }

    viewedRef.current = true;
    trackProductEvent('paywall_viewed', {
      planMode,
      activeTier,
      billingInterval: billing,
      currentTier,
      surface: 'web',
      subscriptionStateLoading: subscriptionDisplay.isLoadingSubscriptionState,
      ...contextAnalytics,
    });
  }, [
    activeTier,
    billing,
    contextAnalytics,
    currentTier,
    planMode,
    subscriptionDisplay.isLoadingSubscriptionState,
  ]);

  const close = useCallback(() => {
    const completionHandler = onClose ?? onComplete;
    if (completionHandler) {
      completionHandler();
      return;
    }

    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }
    router.replace('/' as never);
  }, [onClose, onComplete, router]);

  const handleUpgrade = useCallback(async () => {
    const startedAt = Date.now();
    trackProductEvent('paywall_cta_clicked', {
      planMode,
      tier: activeTier,
      billingInterval: billing,
      currentTier,
      action: showManageInsteadOfCheckout ? 'manage' : 'purchase',
      surface: 'web',
      ...contextAnalytics,
    });

    try {
      if (subscriptionDisplay.isLoadingSubscriptionState) {
        trackProductEvent('paywall_cta_ignored', {
          planMode,
          tier: activeTier,
          billingInterval: billing,
          reason: 'subscription_state_loading',
          surface: 'web',
        });
        return;
      }

      if (showManageInsteadOfCheckout) {
        trackProductEvent('paywall_manage_opened', {
          planMode,
          currentTier,
          surface: 'web',
        });
        if (onClose) {
          onClose();
          settingsModal.open();
          return;
        }

        router.replace('/settings' as never);
        return;
      }

      if (planMode === 'employer' && !companyId) {
        trackProductEvent('paywall_purchase_blocked', {
          planMode,
          tier: activeTier,
          billingInterval: billing,
          reason: 'missing_company',
          surface: 'web',
        });
        throw new Error(t('upgrade.companyRequired'));
      }

      if (resolvedContext) {
        persistPendingUpgradeContext(resolvedContext);
      }

      const result = await startPurchase({
        planSet: planMode,
        tier: activeTier,
        billingInterval: billing,
        companyId: planMode === 'employer' ? (companyId ?? null) : null,
        surface: 'web',
      });

      if (result === 'purchased') {
        trackProductEvent('paywall_purchase_completed', {
          planMode,
          tier: activeTier,
          billingInterval: billing,
          durationMs: Math.max(Date.now() - startedAt, 0),
          surface: 'web',
          ...contextAnalytics,
        });
        toast.success(t('upgrade.purchaseSuccess'));
        if (resolvedContext?.returnTo) {
          trackProductEvent('blocked_action_resumed', {
            surface: 'web',
            ...contextAnalytics,
          });
          router.replace(resolvedContext.returnTo as never);
        } else {
          close();
        }
      } else {
        trackProductEvent('paywall_checkout_redirected', {
          planMode,
          tier: activeTier,
          billingInterval: billing,
          durationMs: Math.max(Date.now() - startedAt, 0),
          surface: 'web',
        });
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('upgrade.purchaseFailed');
      if (!message.toLowerCase().includes('cancel')) {
        trackProductEvent('paywall_purchase_failed', {
          planMode,
          tier: activeTier,
          billingInterval: billing,
          durationMs: Math.max(Date.now() - startedAt, 0),
          surface: 'web',
          ...getErrorObservabilityProperties(error),
        });
        captureProductException(error, {
          feature: 'paywall',
          action: 'purchase',
          planMode,
          tier: activeTier,
          billingInterval: billing,
          surface: 'web',
        });
        toast.error(t('upgrade.purchaseFailed'), message);
      } else {
        trackProductEvent('paywall_purchase_cancelled', {
          planMode,
          tier: activeTier,
          billingInterval: billing,
          durationMs: Math.max(Date.now() - startedAt, 0),
          surface: 'web',
        });
      }
    }
  }, [
    activeTier,
    billing,
    close,
    companyId,
    contextAnalytics,
    onClose,
    currentTier,
    planMode,
    router,
    resolvedContext,
    settingsModal,
    showManageInsteadOfCheckout,
    startPurchase,
    subscriptionDisplay.isLoadingSubscriptionState,
    t,
    toast,
  ]);

  return (
    <WebDialog
      title={contextTitle}
      onClose={close}
      maxWidth={580}
      scrollable
      // The upgrade modal can be summoned from inside another dialog (e.g. the
      // create-skill dialog at zIndex 9999 when a Pro model is picked), so it
      // must paint above that layer: but below the alert host (100000).
      zIndex={10000}
      footer={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
          }}
        >
          {/* Billing toggle */}
          <div
            style={{
              display: 'flex',
              borderRadius: 9999,
              padding: 3,
              backgroundColor: 'rgba(0,0,0,0.04)',
            }}
          >
            {(['monthly', 'yearly'] as BillingPeriod[]).map((period) => (
              <button
                key={period}
                onClick={() => {
                  if (period === billing) {
                    return;
                  }

                  trackProductEvent('paywall_billing_selected', {
                    planMode,
                    tier: activeTier,
                    billingInterval: period,
                    previousBillingInterval: billing,
                    currentTier,
                    surface: 'web',
                  });
                  setBilling(period);
                }}
                style={{
                  paddingInline: 16,
                  paddingBlock: 7,
                  borderRadius: 9999,
                  border: 'none',
                  minWidth: period === 'yearly' ? 160 : 92,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  backgroundColor:
                    billing === period ? 'var(--bg)' : 'transparent',
                  color:
                    billing === period
                      ? 'var(--text-primary)'
                      : 'var(--text-tertiary)',
                  transition: 'background-color 150ms, color 150ms',
                }}
              >
                {t(
                  `upgrade.billing${period.charAt(0).toUpperCase() + period.slice(1)}` as any,
                )}
                {period === 'yearly' ? (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color:
                        billing === period
                          ? 'var(--text-primary)'
                          : 'var(--text-tertiary)',
                      opacity: billing === period ? 1 : 0.85,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('upgrade.billingYearlyBonus')}
                  </span>
                ) : null}
              </button>
            ))}
          </div>

          {/* CTA button */}
          <button
            onClick={() => {
              void handleUpgrade();
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            style={{
              width: '100%',
              height: 52,
              borderRadius: 9999,
              border: 'none',
              backgroundColor: 'var(--text-primary)',
              color: 'var(--bg)',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'opacity 150ms',
              opacity: isBusy ? 0.7 : 1,
            }}
            disabled={isBusy}
          >
            {ctaLabel}
          </button>

          <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
            {t('upgrade.disclaimer')}
          </span>

          {onSkip ? (
            <button
              type="button"
              onClick={onSkip}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--text-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--text-secondary)';
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--text-secondary)',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
                transition: 'color 150ms',
              }}
            >
              {t('upgrade.continueFree')}
            </button>
          ) : null}
        </div>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: 8,
          gap: 4,
        }}
      >
        <UpgradeHeroIcon tier={activeTier} size={40} />

        <p
          style={{
            maxWidth: 460,
            margin: '4px 0 0',
            textAlign: 'center',
            color: 'var(--text-secondary)',
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          {contextSubtitle}
        </p>

        {allowanceCopy ? (
          <div
            style={{
              borderRadius: 9999,
              padding: '8px 14px',
              backgroundColor: 'var(--alpha-05)',
              color: 'var(--text-primary)',
              fontSize: 12,
              lineHeight: 1.4,
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            {allowanceCopy}
          </div>
        ) : null}

        {currentTier !== 'free' &&
        !subscriptionDisplay.isLoadingSubscriptionState ? (
          <p
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text-primary)',
              margin: 0,
            }}
          >
            {subscriptionDisplay.fullPlanLabel}
          </p>
        ) : null}

        {/* Plus / Pro toggle */}
        <div
          style={{
            display: 'flex',
            borderRadius: 9999,
            padding: 3,
            backgroundColor: 'rgba(0,0,0,0.04)',
            marginTop: 16,
            marginBottom: 24,
          }}
        >
          {(['plus', 'pro'] as Tier[]).map((tier) => (
            <button
              key={tier}
              onClick={() => {
                if (currentTier === 'pro' && tier === 'plus') {
                  return;
                }

                if (tier !== activeTier) {
                  trackProductEvent('paywall_tier_selected', {
                    planMode,
                    selectedTier: tier,
                    previousTier: activeTier,
                    currentTier,
                    surface: 'web',
                  });
                }
                setActiveTier(tier);
              }}
              disabled={currentTier === 'pro' && tier === 'plus'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingInline: 20,
                paddingBlock: 8,
                borderRadius: 9999,
                border: 'none',
                minWidth: 72,
                cursor:
                  currentTier === 'pro' && tier === 'plus'
                    ? 'not-allowed'
                    : 'pointer',
                fontSize: 15,
                fontWeight: 600,
                backgroundColor:
                  activeTier === tier ? 'var(--bg)' : 'transparent',
                color:
                  activeTier === tier
                    ? 'var(--text-primary)'
                    : 'var(--text-tertiary)',
                transition: 'background-color 150ms, color 150ms',
                opacity: currentTier === 'pro' && tier === 'plus' ? 0.45 : 1,
              }}
            >
              {t(
                `upgrade.tab${tier.charAt(0).toUpperCase() + tier.slice(1)}` as any,
              )}
            </button>
          ))}
        </div>

        {/* Feature table */}
        <div
          style={{
            width: '100%',
            borderRadius: 12,
            border: '1px solid var(--input-border)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingInline: 16,
              paddingBlock: 12,
              borderBottom: '1px solid var(--input-border)',
            }}
          >
            <span
              style={{
                flex: 1,
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--text-tertiary)',
              }}
            >
              {t('upgrade.featuresHeader')}
            </span>
            <span
              style={{
                width: 100,
                textAlign: 'center',
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--text-tertiary)',
              }}
            >
              {config.freeColumnLabel}
            </span>
            <span
              style={{
                width: 100,
                textAlign: 'center',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              {config.paidColumnLabel}
            </span>
          </div>

          {/* Rows */}
          {config.features.map((feature, index) => {
            const isLast = index === config.features.length - 1;
            const tierMark = getFeatureTierMark(activeTier, feature.key);
            return (
              <div
                key={feature.key}
                onMouseEnter={(e) => setHoverBg(e, HOVER_BG)}
                onMouseLeave={(e) => setHoverBg(e, 'transparent')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingInline: 16,
                  paddingBlock: 10,
                  minHeight: 44,
                  borderBottom: isLast
                    ? 'none'
                    : '1px solid var(--input-border)',
                  transition: 'background-color 150ms',
                }}
              >
                <span
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 0,
                    fontSize: 14,
                    lineHeight: 1.25,
                    color: 'var(--text-primary)',
                  }}
                >
                  {feature.modelBrand ? (
                    <AgentModelMark brand={feature.modelBrand} size={18} />
                  ) : tierMark ? (
                    <FeatureTierIcon mark={tierMark} />
                  ) : null}
                  <span style={{ minWidth: 0 }}>{feature.label}</span>
                </span>
                <span
                  style={{
                    width: 100,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <FeatureColumnValue value={feature.free} />
                </span>
                <span
                  style={{
                    width: 100,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <FeatureColumnValue value={feature.paid} />
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </WebDialog>
  );
}
